import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import Cookies from 'js-cookie';

import './skillstestmaster.css';
import Handtyping from '../assets/hand-typing-test.png';
import { MdNextPlan } from 'react-icons/md';
import { BiReset } from 'react-icons/bi';
import { documentId } from 'firebase/firestore';
import Header from '../body_root/header';
import Footers from '../body_root/footer';
import Navbar from '../body_root/Navbar';
import ShareButton from '../components_root/ShareButton';
const KEYBOARD_ROWS = [
  ['Esc', 'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12', 'Del'],
  ['`', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '=', 'Backspace'],
  ['Tab', 'Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', '[', ']', '\\'],
  ['Caps', 'A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', ';', '\'', 'Enter'],
  ['Shift', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', ',', '.', '/', 'Shift'],
  ['Ctrl', 'Win', 'Alt', 'Space', 'Alt', 'Win', 'Menu', 'Ctrl']
];

const educationalContent = [
  {
    title: "J, F, and Space",
    content: "Start your typing journey by practicing the J and F keys, which are the anchor points for your index fingers. Use the spacebar with your thumbs to build rhythm.",
    showFingerPlacement: true
  },
  {
    title: "U, R, and K Keys",
    content: "Learn to stretch your index and middle fingers to reach the U, R, and K keys while maintaining home row position.",
    showFingerPlacement: true
  },
  {
    title: "D, E, and I Keys",
    content: "Build dexterity with the D, E, and I keys. These are common letters, and practicing them will improve both accuracy and speed.",
    showFingerPlacement: true
  },
  {
    title: "C, G, and N Keys",
    content: "These keys require lateral finger movement. Focus on precision and returning to home row after each stroke.",
    showFingerPlacement: true
  },
  {
    title: "Beginner Review 1",
    content: "Review all previously learned keys in a combined practice session. Reinforce memory and finger positioning.",
    showFingerPlacement: true
  },
  {
    title: "T, S, and L Keys",
    content: "Practice T, S, and L which are positioned on both left and right hands. This helps balance hand coordination.",
    showFingerPlacement: true
  },
  {
    title: "O, B, and A Keys",
    content: "Focus on the outer edge keys to improve reach and movement. These letters are frequent in English writing.",
    showFingerPlacement: true
  },
  {
    title: "V, H, and M Keys",
    content: "Practice mid-row keys that involve both inner and outer finger control for fluent typing.",
    showFingerPlacement: true
  },
  {
    title: "Period and Comma",
    content: "Learn punctuation basics using your ring and little fingers. Timing and flow matter when typing full sentences.",
    showFingerPlacement: true
  },
  {
    title: "Beginner Review 2",
    content: "Review all letter and punctuation keys you've learned so far. Focus on accuracy and consistency.",
    showFingerPlacement: true
  },
  {
    title: "W, X, and ; Keys",
    content: "These are tricky keys. Pay extra attention to proper finger reach and form to avoid strain.",
    showFingerPlacement: true
  },
  {
    title: "Q, Y, and P Keys",
    content: "These corner keys challenge your hand stretch. Practice slowly and consistently to gain confidence.",
    showFingerPlacement: true
  },
  {
    title: "Z and Enter Keys",
    content: "Master the Z key with your little finger and practice pressing Enter confidently after finishing lines.",
    showFingerPlacement: true
  },
  {
    title: "Beginner Wrap-up",
    content: "Finalize your beginner course by revisiting all major keys. This lesson prepares you for the final assessment.",
    showFingerPlacement: true
  },
  {
    title: "Beginner Assessment",
    content: "Test your typing skills with a timed assessment. Measure your WPM and accuracy to see your progress.",
    showFingerPlacement: true
  },
  {
    title: "Step 1: Learn Finger Placement",
    content: "Proper finger placement is crucial for improving your typing speed. Start by placing your fingers on the home row keys: 'ASDF' for the left hand and 'JKL;' for the right hand.",
    showFingerPlacement: true
  },
  {
    title: "Step 2: Improve Accuracy",
    content: "Accuracy is more important than speed. Focus on typing each letter correctly and slowly at first. Mistakes can reduce your speed in the long run.",
    showFingerPlacement: false
  },
  {
    title: "Step 3: Focus on Speed",
    content: "Once you are comfortable with accuracy, start focusing on increasing your speed. Practice typing without looking at the keyboard.",
    showFingerPlacement: false
  },
  {
    title: "Step 4: Advanced Typing Techniques",
    content: "To improve further, practice typing without using the backspace key. This will help you overcome the habit of correcting errors and improve flow.",
    showFingerPlacement: false
  },
  {
    title: "Step 5: Master Number and Symbol Keys",
    content: "Learn to type numbers and common symbols (!, @, #, etc.) accurately using the correct fingers. This expands your typing capabilities, especially for coding or data entry.",
    showFingerPlacement: false
  },
  {
    title: "Step 6: Practice with Real Text",
    content: "Practice typing passages from books, articles, or your own writing. This helps you adapt to different vocabulary and sentence structures.",
    showFingerPlacement: false
  },
  {
    title: "Step 7: Use Typing Software or Games",
    content: "Typing games and software provide engaging ways to improve your skills. Use them regularly to track progress and stay motivated.",
    showFingerPlacement: false
  },
  {
    title: "Step 8: Monitor Progress",
    content: "Regularly measure your WPM (words per minute) and accuracy rate. Set small goals to keep improving over time.",
    showFingerPlacement: false
  },
  {
    title: "Step 9: Develop Muscle Memory",
    content: "Consistent practice is key to building muscle memory. Set aside 15–30 minutes daily to type and reinforce what you've learned.",
    showFingerPlacement: false
  },
  {
    title: "Step 10: Maintain Proper Posture",
    content: "Good posture reduces fatigue and helps you type longer with less strain. Sit straight, keep your wrists elevated, and position the keyboard at a comfortable height.",
    showFingerPlacement: false
  }
];

const getFingerClass = (key) => {
  const pinkyLeft = ['`', '1', 'Q', 'A', 'Z', 'Tab', 'Caps', 'Shift', 'Ctrl'];
  const ringLeft = ['2', 'W', 'S', 'X'];
  const middleLeft = ['3', 'E', 'D', 'C'];
  const indexLeft = ['4', '5', 'R', 'T', 'F', 'G', 'V', 'B'];

  const indexRight = ['6', '7', 'Y', 'U', 'H', 'J', 'N', 'M'];
  const middleRight = ['8', 'I', 'K', ','];
  const ringRight = ['9', 'O', 'L', '.'];
  const pinkyRight = ['0', '-', '=', '[', ']', '\\', 'P', ';', '\'', '/', 'Enter', 'Backspace', 'Shift', 'Ctrl'];
  const thumbs = ['Space'];

  if (pinkyLeft.includes(key)) return 'pinky-left';
  if (ringLeft.includes(key)) return 'ring-left';
  if (middleLeft.includes(key)) return 'middle-left';
  if (indexLeft.includes(key)) return 'index-left';
  if (indexRight.includes(key)) return 'index-right';
  if (middleRight.includes(key)) return 'middle-right';
  if (ringRight.includes(key)) return 'ring-right';
  if (pinkyRight.includes(key)) return 'pinky-right';
  if (thumbs.includes(key)) return 'thumbs';
  return '';
};

export default function Typing_master() {
  const [pressedKey, setPressedKey] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [typedText, setTypedText] = useState('');
  const [wpm, setWpm] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const sampleText = "The quick brown fox jumps over the lazy dog.";
  const textAreaRef = useRef(null);
  const [user, setUser] = useState(null);
  const [guid, setGuid] = useState("");
  const navigate = useNavigate();
  const [loginisOpen, loginsetIsOpen] = useState(false);
  const [Usersloginlist, setUsersloginlist] = useState([]);
  useEffect(() => {
    // checkUserAuth(setUser);
    const existingUser = Cookies.get("guid");
    if (existingUser != null) {
      setGuid(existingUser);
      loginsetIsOpen(true);
    } else {
      loginsetIsOpen(false);
    }
  }, []);



  useEffect(() => {
    let interval;
    if (timerRunning) {
      interval = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [timerRunning]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      let keyPressed = e.key;

      if (keyPressed === " ") keyPressed = "Space";
      if (keyPressed === "Enter") keyPressed = "Enter";
      if (keyPressed === "Backspace") keyPressed = "Backspace";
      if (keyPressed === "Tab") keyPressed = "Tab";
      if (keyPressed === "Shift") keyPressed = "Shift";
      if (keyPressed === "Control") keyPressed = "Ctrl";
      if (keyPressed === "Meta") keyPressed = "Win";
      if (keyPressed === "Alt") keyPressed = "Alt";

      setPressedKey(keyPressed.toUpperCase());
    };

    const handleKeyUp = () => {
      setTimeout(() => setPressedKey(null), 100);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const handleTyping = (e) => {
    const value = e.target.value;

    if (!startTime) {
      setStartTime(Date.now());
      setTimerRunning(true);
    }

    setTypedText(value);

    const elapsedMinutes = (Date.now() - startTime) / 1000 / 60;
    const wordsTyped = value.length / 5;
    const calculatedWPM = elapsedMinutes > 0 ? Math.round(wordsTyped / elapsedMinutes) : 0;

    setWpm(calculatedWPM);
  };
  useEffect(() => {
    document.getElementsByClassName('typing-input')[0].focus();
    if (textAreaRef.current) {
      // Reset the height to 'auto' to shrink if needed
      textAreaRef.current.style.height = 'auto';
      // Set the height to scrollHeight to expand it based on content
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }
  }, [typedText]); // This effect runs every time the text changes

  const calculateAccuracy = () => {
    let correct = 0;
    for (let i = 0; i < typedText.length; i++) {
      if (typedText[i] === sampleText[i]) correct++;
    }
    return typedText.length > 0 ? Math.round((correct / typedText.length) * 100) : 100;
  };

  const resetTest = () => {
    setStartTime(null);
    setTypedText('');
    setWpm(0);
    setPressedKey(null);
    setElapsedSeconds(0);
    setTimerRunning(false);
    setCurrentStep(0);
  };
  const nextStep = () => {
    if (currentStep < educationalContent.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  function formatTime(seconds) {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return `${hrs}:${mins.toString().padStart(2, '0')} ${secs.toString().padStart(2, '0')}`;
  }
useEffect(() => {
  const inputEl = document.querySelector(".typing-input");
  if (inputEl) {
    inputEl.focus();
  }
}, []);
  return (
    <div>
      <Header />
      <div className="app_header_height"></div>
      <div className="container_roots">
        <Helmet>
          <title>Typing Master online free | Typing Skills Test | Improve Typing Speed</title>
          <meta name="description" content="Typing Master online free,Improve Typing Speed, Test your typing speed and accuracy . Choose your difficulty level and track your progress with detailed results and insights!" />
          <meta name="keywords" content="Typing Master online, typing test,Improve Typing Speed, typing speed, accuracy test, typing practice, improve typing skills, WPM, typing test online, typing master" />
          <meta property="og:title" content="Typing Master online free | Typing Skills Test | Improve Typing Speed" />
          <meta property="og:description" content="Typing Master online free, Test your typing speed and accuracy with Typing Master. Choose your difficulty level and track your progress with detailed results and insights!" />
          <meta property="og:url" content="https://viabig.com/typing-master" />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://www.viabig.com/" />

        </Helmet>
        <div className="container_size p-2">
          <br />
          <div className="post-container">
            <div className='post_div '>
              <b className="text-h2">Typing Master Online Free | Typing Skills Test | Improve Typing Speed</b>
            </div>
            <div className='post_updates'>
              <ShareButton url={'https://viabig.com/typing-master'} />  </div>
          </div>
          <br />
          <div className="post-container">
            <div className='post_div '>
              <div className="instructions">
                <b className='step-text'>{educationalContent[currentStep].title}</b>
                <div className='currentStep-text'>{educationalContent[currentStep].content}</div>
              </div>
            </div>
          </div>
          <br />
          <div className="post-container">
            <div className='post_div '>
              <textarea
                value={typedText}
                ref={textAreaRef}
                onChange={handleTyping}
                placeholder="Start typing here..."
                className="typing-input"
                autoComplete="off"
                autoCorrect="off"
                rows={1}
                cols={1}
              />
            </div>
          </div>     <br />
          <div className="post-container">
            <div className='post_div '>
              <table className='wpm-display items-back-fill'>
                <tr>
                  <td className='output-div text-h2'>Words per minute: <b>{wpm}</b> </td>
                  <td className='output-div text-h2'>Accuracy: <b>{calculateAccuracy()}%</b></td>
                </tr>
                <tr>
                  <td className='output-div'>
                    {
                      loginsetIsOpen === false ? (
                        <div>Not logged in</div>
                      ) : (
                        <div>Date: {new Date().toLocaleString()}</div>
                      )
                    }
                  </td>

                  <td className='output-div'>Time: {formatTime(elapsedSeconds)}</td>
                </tr>

              </table>
            </div>
            <div className='post_updates'>
              <table width={`100%`}>
                <tr>
                  <td>  {currentStep < educationalContent.length - 1 && (
                    <button onClick={nextStep} className="button-ui p-3"><MdNextPlan className='icon' /> Next</button>
                  )}</td>
                  <td><button className='button-ui p-3' onClick={resetTest}><BiReset className='icon' /> Restart</button></td>
                </tr>
              </table>
            </div>
          </div>
          <div className="keyboard p-2">
            {KEYBOARD_ROWS.map((row, i) => (
              <div key={i} className="keyboard-row">
                {row.map((key) => (
                  <div
                    key={key}
                    className={`keyboard-key ${getFingerClass(key)} ${pressedKey === key.toUpperCase() ? 'pressed' : ''}`}
                  >
                    {key}
                  </div>
                ))}
              </div>
            ))}
          </div>

        </div>
        <div className="container-Handtyping">
          <img className="Handtyping" src={Handtyping} alt="Handtyping" />
        </div>
        <br />

        <div className="container_size">
          <h2>Typing Master Online Free | Typing Skills Test | Improve Typing Speed</h2>
          <p>Improve your typing speed and accuracy with our comprehensive typing test. Whether you're a beginner or a seasoned typist, our test adapts to your level, helping you measure progress and achieve typing mastery.</p>

          <h3>Key Features:</h3>
          <ul>
            <li><strong>Real-Time Speed & Accuracy Tracking:</strong> Measure your words per minute (WPM) and accuracy in real-time.</li>
            <li><strong>Finger Placement Guide:</strong> Learn optimal finger positions with our visual keyboard.</li>
            <li><strong>Multiple Difficulty Levels:</strong> Start from beginner to advanced with tests designed to challenge you at every stage.</li>
            <li><strong>Customizable Text Options:</strong> Choose from various text themes for a personalized experience.</li>
            <li><strong>Instant Results & Insights:</strong> Get detailed reports on your typing performance, including speed and error rates.</li>
          </ul>

          <h3>Tips for Success:</h3>
          <ul>
            <li><strong>Practice Regularly:</strong> Consistent practice is key to improving both speed and accuracy.</li>
            <li><strong>Maintain Proper Posture:</strong> A comfortable and ergonomic typing setup can reduce strain and increase speed.</li>
            <li><strong>Focus on Accuracy First:</strong> Speed will naturally improve as you focus on typing accurately.</li>
            <li><strong>Use All Fingers:</strong> Employ all your fingers for better coordination and faster typing.</li>
            <li><strong>Take Breaks:</strong> Avoid fatigue by taking short breaks to refresh your hands and mind.</li>
          </ul>

          <p>Start mastering your typing skills today and boost your productivity!</p>
        </div>

      </div>
      <Footers />
      <Navbar />
    </div>
  );
}
